
import React from "react";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Schemes, Subscriber, Utils } from "../../../../utils";
import { ConfigurationInputs, ConfigurationValue } from "./ConfigUtils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { BoolHelper, ButtonManageRef, Classname, ObjectHelper, Scheme } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ConfigurationType, ICountryConfigurationMapping, CountryConfigurationAndMappingService, SanitizedResponse, ICountryConfiguration, CountryService, ICountry } from "../../../../services";

function CountryConfigurationMappings() {
    const countryService = CountryService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const labels = i18nManager.Labels.dashboard.ploutous.configurations.country_mappings_configs;
    const countryConfigurationAndMappingService = CountryConfigurationAndMappingService.getInstance();
    const [activeCountryConfigurationMapping, setActiveCountryConfigurationMapping] = React.useState<ICountryConfigurationMapping>();
    const [countryConfigurationMappings, setCountryConfigurationMappings] = React.useState<ApiResponseData<ICountryConfigurationMapping>>({});

    return SidedContainer({
        searchParams,
        setSearchParams,
        labels: labels.sided_labels,
        data: countryConfigurationMappings,
        onAction: manageCountryConfigurationMapping,
        queryData: fetchCountryConfigurationMappings,
        onLoading: (done: () => void) => {
            countryConfigurationAndMappingService.queryCountryConfigurations({ size: 20 }).then(({ apiResponse }: SanitizedResponse<ICountryConfiguration>) => {
                labels.dialogs.manage.form.find((f) => f.name === "configuration_id")!.options = apiResponse.data!.content?.map((c) => ({ label: c.key, value: c.external_id }));
                labels.sided_labels.dropdown_form!.find((f) => f.control.name === "configuration_id")!.control.options =
                    [{ label: i18nManager.Labels.dashboard.ploutous.configurations.all, value: "" }].concat(apiResponse.data!.content?.map((c) => ({ label: c.key, value: c.external_id })) ?? []);
                countryService.queryCountries({ size: 20 }).then(({ apiResponse }: SanitizedResponse<ICountry>) => {
                    labels.dialogs.manage.form.find((f) => f.name === "country_id")!.options = apiResponse.data!.content?.map((c) => ({ label: c.name, value: c.external_id }));
                    labels.sided_labels.dropdown_form!.find((f) => f.control.name === "country_id")!.control.options =
                        [{ label: i18nManager.Labels.dashboard.ploutous.configurations.all, value: "" }].concat(apiResponse.data!.content?.map((c) => ({ label: c.name, value: c.external_id })) ?? []);
                    done();
                }).catch(BaseService.reportError)
            }).catch(BaseService.reportError)
        },
        activeData: (activeCountryConfigurationMapping ? {
            data: activeCountryConfigurationMapping,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveCountryConfigurationMapping(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeCountryConfigurationMapping?.country_configuration?.key}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeCountryConfigurationMapping?.external_id}</span>
                    <span className="id">{activeCountryConfigurationMapping?.type}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0, borderBottom: "none", paddingBottom: 0 }}>
                    <span style={{ marginTop: 5 }}><b>{i18nManager.Labels.dashboard.ploutous.configurations.type_options.find((t) => t.value === activeCountryConfigurationMapping.type)?.label}</b></span>
                    <ConfigurationValue type={activeCountryConfigurationMapping.type} value={activeCountryConfigurationMapping.value} />
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfigurationMapping(activeCountryConfigurationMapping)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeCountryConfigurationMapping?.created_by?.first_name} {activeCountryConfigurationMapping?.created_by?.last_name} ({activeCountryConfigurationMapping?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfigurationMapping(activeCountryConfigurationMapping)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeCountryConfigurationMapping?.updated_by?.first_name} {activeCountryConfigurationMapping?.updated_by?.last_name} ({activeCountryConfigurationMapping?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfigurationMapping(activeCountryConfigurationMapping)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeCountryConfigurationMapping?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCountryConfigurationMapping(activeCountryConfigurationMapping)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeCountryConfigurationMapping?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageCountryConfigurationMapping(activeCountryConfigurationMapping)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {/* {activeCountryConfigurationMapping?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateCountryConfigurationMapping(activeCountryConfigurationMapping, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeCountryConfigurationMapping?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateCountryConfigurationMapping(activeCountryConfigurationMapping, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeCountryConfigurationMapping?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteCountryConfigurationMapping(activeCountryConfigurationMapping)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)} */}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (countryConfigurationMapping: ICountryConfigurationMapping) => {
            return (<div className={Classname.build("channel", (activeCountryConfigurationMapping?.external_id === countryConfigurationMapping.external_id ? "active" : null))} onClick={() => {
                setActiveCountryConfigurationMapping(countryConfigurationMapping);
                Utils.updateSearchParams("active_data_id", countryConfigurationMapping.external_id, setSearchParams);
            }}>
                <div className="id" style={countryConfigurationMapping.country_configuration.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <span className="name" style={{ marginRight: 5 }}>{countryConfigurationMapping.country_configuration.key}</span> <span className="type">- {countryConfigurationMapping.type}</span>
                </div>
                <span className="desc">{countryConfigurationMapping.country_configuration.description}</span>
                <div className="ctrls">
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageCountryConfigurationMapping(countryConfigurationMapping)} />
                </div>
            </div>);
        }
    });

    function fetchCountryConfigurationMappings() {
        const id = searchParams.get("active_data_id");
        countryConfigurationAndMappingService.queryCountryConfigurationMappings(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ICountryConfigurationMapping>) => {
            setCountryConfigurationMappings(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveCountryConfigurationMapping(item);
                    //manageCountryConfigurationMapping(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCountryConfigurationMapping(_countryConfigurationMapping: ICountryConfigurationMapping = {} as any) {
        let changeProps: (type: ConfigurationType, v?: any) => void;
        const countryConfigurationMapping: ICountryConfigurationMapping = ObjectHelper.clone(_countryConfigurationMapping);
        const basics = labels.dialogs.manage.basic[countryConfigurationMapping ? "update" : "create"];
        const renderValueComponent = !BoolHelper.equalsAny(countryConfigurationMapping.type, [ConfigurationType.TEXT, ConfigurationType.NUMBER]);

        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: (renderValueComponent ? 800 : 400), maxWidth: "80%" },
            content: (<div className="add-new-role" ref={(_) => {
                changeProps?.(countryConfigurationMapping.type, countryConfigurationMapping.value);
            }}>
                {Elements.buildConfirmDialogForm(labels.dialogs.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    (countryConfigurationMapping as any)[name] = value;
                    if (name === "type") {
                        changeProps?.(value as ConfigurationType, countryConfigurationMapping.value);
                    }
                }, countryConfigurationMapping, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            rightContent: (renderValueComponent ? (<div style={{ display: "flex", flexDirection: "column", height: "100%", borderLeft: "1px solid #F0F0F0", marginTop: 30, marginLeft: 5, paddingLeft: 25 }}>
                <ConfigurationInputs type={ConfigurationType.TEXT} value={countryConfigurationMapping.value} changeProps={(cb) => changeProps = cb} onNewValue={(v) => {
                    countryConfigurationMapping.value = v;
                }} excludes={[ConfigurationType.TEXT, ConfigurationType.NUMBER]} />
            </div>) : undefined),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                if (Object.keys(countryConfigurationMapping).length < 5) {
                    errorMessage = labels.dialogs.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!countryConfigurationMapping.external_id ? countryConfigurationAndMappingService.updateCountryConfigurationMapping : countryConfigurationAndMappingService.createCountryConfigurationMapping).bind(countryConfigurationAndMappingService);
                method(countryConfigurationMapping)
                    .then(({ }: SanitizedResponse<ICountryConfigurationMapping>) => {
                        Siren.alert(basics.successful);
                        dialog.hide(fetchCountryConfigurationMappings);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function deleteCountryConfigurationMapping(countryConfigurationMapping: ICountryConfigurationMapping) {
        const dialog = ConfirmDialog({
            closable: true,
            confirmScheme: Scheme.DANGER,
            desc: labels.dialogs.delete.desc,
            title: labels.dialogs.delete.title,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                countryConfigurationAndMappingService.deleteCountryConfigurationMapping(countryConfigurationMapping.external_id).then(() => {
                    Siren.alert(labels.dialogs.delete.successful);
                    dialog.hide(() => setActiveCountryConfigurationMapping(undefined));
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default CountryConfigurationMappings;

